import axios from 'axios'
import firebase from 'firebase/compat/app'

const apiClient = axios.create({
    baseURL: `${import.meta.env.VITE_API_URL}`,
    // Ensure cookies like 'distinctId' are sent for aliasing
    withCredentials: true,
    headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
    },
})

// -----------------------------
// Auth readiness gate (mobile-safe)
// -----------------------------
function createDeferred() {
    let resolve
    let reject
    const promise = new Promise((res, rej) => {
        resolve = res
        reject = rej
    })
    return { promise, resolve, reject }
}

let authReady = false
let authDeferred = createDeferred()

function signalAuthReady() {
    if (!authReady) {
        authReady = true
        try {
            authDeferred.resolve()
        } catch (_) {}
        try {
            document.dispatchEvent(new CustomEvent('auth:ready'))
        } catch (_) {}
    }
}

function resetAuthGate() {
    authReady = false
    authDeferred = createDeferred()
}

async function waitUntilAuthReady(timeoutMs = 15000) {
    if (authReady) return
    let timer
    try {
        await Promise.race([
            authDeferred.promise,
            new Promise((_, rej) => {
                timer = setTimeout(
                    () => rej(new Error('AUTH_NOT_READY')),
                    timeoutMs
                )
            }),
        ])
    } finally {
        if (timer) clearTimeout(timer)
    }
}

// Wire Firebase auth events to the gate
try {
    firebase.auth().onAuthStateChanged((user) => {
        if (user) signalAuthReady()
        else resetAuthGate()
    })
    firebase.auth().onIdTokenChanged((user) => {
        // Reset token cache when token updates
        if (user) {
            tokenManager.resetTokenManager()
            signalAuthReady()
        }
    })
} catch (_) {}

// Add a token manager
const tokenManager = {
    token: null,
    expirationTime: 0,
    MAX_RETRIES: 16,
    RETRY_DELAY_MS: 1000,

    async getToken() {
        const now = Date.now()
        // If token is fresh (not within 5 minutes of expiring), return it
        if (this.token && now + 300000 < this.expirationTime) {
            return this.token
        }

        // Ensure auth is ready (avoid mobile resume race)
        const userAtStart = firebase.auth().currentUser
        if (!userAtStart) {
            await waitUntilAuthReady()
        }

        // Otherwise, try to fetch a new token with retries
        for (let attempt = 1; attempt <= this.MAX_RETRIES; attempt++) {
            try {
                const user = firebase.auth().currentUser
                if (!user) {
                    // If auth still not available, wait a bit and retry
                    await this._sleep(this.RETRY_DELAY_MS)
                    continue
                }

                const newToken = await user.getIdToken(true)
                const decoded = await user.getIdTokenResult()
                this.token = newToken
                this.expirationTime = new Date(decoded.expirationTime).getTime()
                return this.token
            } catch (err) {
                console.error(
                    `Error getting auth token (attempt ${attempt}):`,
                    err
                )
                if (attempt < this.MAX_RETRIES) {
                    await this._sleep(this.RETRY_DELAY_MS)
                } else {
                    console.error(
                        'Token unavailable after retries, signing out...'
                    )
                    firebase
                        .auth()
                        .signOut()
                        .catch((e) => console.error('SignOut failed:', e))
                    throw new Error('TOKEN_UNAVAILABLE')
                }
            }
        }
    },

    resetTokenManager() {
        this.token = null
        this.expirationTime = 0
    },

    async forceRefresh() {
        const user = firebase.auth().currentUser
        if (!user) {
            await waitUntilAuthReady()
        }
        const ensuredUser = firebase.auth().currentUser
        if (!ensuredUser) throw new Error('AUTH_NOT_READY')
        const newToken = await ensuredUser.getIdToken(true)
        const decoded = await ensuredUser.getIdTokenResult()
        this.token = newToken
        this.expirationTime = new Date(decoded.expirationTime).getTime()
        return this.token
    },

    _sleep(ms) {
        return new Promise((resolve) => setTimeout(resolve, ms))
    },
}

// Add an interceptor to automatically add the token to requests if available
apiClient.interceptors.request.use(
    async (config) => {
        // Skip auth if skipAuth flag is set in config
        if (config.skipAuth) {
            return config
        }

        try {
            const token = await tokenManager.getToken()
            // Never send protected requests without token
            if (!token) {
                throw new Error('AUTH_TOKEN_MISSING')
            }
            config.headers.idtoken = token
        } catch (e) {
            console.log('Error in request interceptor', e)
            // Reject to avoid sending unauthenticated requests
            return Promise.reject(e)
        }
        // If we got here, token is set
        return config
    },
    (error) => Promise.reject(error)
)

// Single retry on 401/403 with forced refresh
apiClient.interceptors.response.use(
    (response) => response,
    async (error) => {
        const status = error?.response?.status
        const originalRequest = error?.config
        if (
            originalRequest &&
            !originalRequest.skipAuth &&
            (status === 401 || status === 403) &&
            !originalRequest.__isRetryRequest
        ) {
            try {
                await tokenManager.forceRefresh()
                const refreshedToken = await tokenManager.getToken()
                if (!refreshedToken) throw new Error('REFRESH_FAILED')
                originalRequest.headers = {
                    ...(originalRequest.headers || {}),
                    idtoken: refreshedToken,
                }
                originalRequest.__isRetryRequest = true
                return apiClient(originalRequest)
            } catch (refreshErr) {
                // Fall through to rejection
            }
        }
        return Promise.reject(error)
    }
)

// Prewarm token on resume/focus to avoid first-action failures
async function prewarmToken() {
    try {
        if (firebase.auth().currentUser) {
            await tokenManager.getToken()
        }
    } catch (_) {}
}

try {
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') prewarmToken()
    })
    window.addEventListener('focus', prewarmToken)
    window.addEventListener('pageshow', prewarmToken)
} catch (_) {}

export { tokenManager }
export default apiClient
